import React, { useState, useEffect, Fragment } from "react";
import { formatTime, formatDateTime, getCityName } from "../utils/flightDataParser";
import { Transition, Listbox } from "@headlessui/react";
import { getAirlineLogo } from "../utils/airlineImages";
import { getCancellationCharges, sendChangeRequest } from "../services/flightBookingAPI";
import { auth } from "../firebase";
import { toast } from "react-toastify";

const CancelBookingDrawer = ({ isOpen, onClose, booking, onSuccess }) => {
    const [selectedFlight, setSelectedFlight] = useState(true);
    const [selectedPassengers, setSelectedPassengers] = useState({});
    const [cancellationType, setCancellationType] = useState("noshow");
    const [remarks, setRemarks] = useState("");
    const [calculatingLoading, setCalculatingLoading] = useState(false);
    const [submittingLoading, setSubmittingLoading] = useState(false);
    const [refundInfo, setRefundInfo] = useState(null);

    // Reset state when booking changes (drawer opens for new booking)
    useEffect(() => {
        setRefundInfo(null);
        setRemarks("");
        setCancellationType("noshow");
    }, [booking?.rc_booking_id || booking?.BookingId]);

    const cancellationOptions = [
        { id: "noshow", name: "No Show", value: "noshow" },
        { id: "cancelled", name: "Flight cancelled", value: "cancelled" },
        { id: "others", name: "Others", value: "others" },
    ];

    // Reset state when drawer opens with new booking
    useEffect(() => {
        if (isOpen && booking) {
            setSelectedFlight(true);
            const ticketDetails = parse(booking.ticket_details);
            const bookingRequest = parse(booking.booking_request);
            const it = ticketDetails?.FlightItinerary ||
                ticketDetails?.Response?.Response?.FlightItinerary ||
                ticketDetails?.Response?.FlightItinerary ||
                bookingRequest?.FlightItinerary ||
                bookingRequest?.Response?.Response?.FlightItinerary ||
                booking?.FlightItinerary ||
                booking?.Response?.Response?.FlightItinerary ||
                booking?.ticket_details?.FlightItinerary ||
                booking?.booking_request?.FlightItinerary;

            const paxList = it?.Passenger || [];
            const newPassengerState = {};
            paxList.forEach((_, idx) => newPassengerState[idx] = true);
            setSelectedPassengers(newPassengerState);
            setCancellationType("noshow");
            setRemarks("");
        }
    }, [isOpen, booking]);

    if (!booking) return null;

    // Helper to parse JSON
    const parse = (data) => {
        if (!data) return {};
        if (typeof data === "object") return data;
        try {
            const first = JSON.parse(data);
            if (typeof first === 'string') return JSON.parse(first);
            return first;
        } catch { return {}; }
    };

    const ticketDetails = parse(booking.ticket_details);
    const bookingRequest = parse(booking.booking_request);
    const itinerary = ticketDetails?.FlightItinerary ||
        ticketDetails?.Response?.Response?.FlightItinerary ||
        ticketDetails?.Response?.FlightItinerary ||
        bookingRequest?.FlightItinerary ||
        bookingRequest?.Response?.Response?.FlightItinerary ||
        booking?.FlightItinerary ||
        booking?.Response?.Response?.FlightItinerary ||
        booking?.ticket_details?.FlightItinerary ||
        booking?.booking_request?.FlightItinerary;

    // Extract segments
    let segments = itinerary?.Segments || [];
    if (segments.length > 0 && Array.isArray(segments[0])) {
        segments = segments[0];
    }

    const firstSeg = segments[0] || {};
    const lastSeg = segments[segments.length - 1] || firstSeg;

    const originCode = firstSeg?.Origin?.Airport?.AirportCode || booking.source || "ORG";
    const destCode = lastSeg?.Destination?.Airport?.AirportCode || booking.destination || "DST";
    const depDate = firstSeg?.Origin?.DepTime || booking.journey_date;

    const airlineCode = firstSeg?.Airline?.AirlineCode;
    const airlineName = firstSeg?.Airline?.AirlineName;

    const passengers = itinerary?.Passenger || [];

    const handlePassengerToggle = (index) => {
        setSelectedPassengers(prev => {
            const currentSelected = Object.values(prev).filter(v => v).length;
            // Safety check: don't let them deselect all passengers
            if (prev[index] && currentSelected <= 1) return prev;

            return {
                ...prev,
                [index]: !prev[index]
            };
        });
    };

    const handleSelectFlight = () => {
        // Safety check: don't let them deselect all sectors
        if (selectedFlight) return;

        setSelectedFlight(true);
        // Auto-select all passengers when flight is re-selected
        const newPassengerState = {};
        passengers.forEach((_, idx) => newPassengerState[idx] = true);
        setSelectedPassengers(newPassengerState);
    };

    const getTboBookingId = () => {
        return booking.tbo_booking_id || ticketDetails?.BookingId || booking.BookingId;
    };

    const handleCalculateRefund = async () => {
        const tboBookingId = getTboBookingId();
        if (!tboBookingId) {
            toast.error("TBO Booking ID not found.", { position: "top-center" });
            return;
        }

        try {
            setCalculatingLoading(true);
            const user = auth.currentUser;
            if (!user) {
                toast.error("Please login to continue", { position: "top-center" });
                return;
            }
            const token = await user.getIdToken();
            const response = await getCancellationCharges(tboBookingId, token);

            if (response.status === 200) {
                const data = response.data || response;
                setRefundInfo(data);

                if (data.raw_response?.ResponseStatus === 2 || data.ResponseStatus === 2) {
                    if (data.refund_amount || data.refund_amount === 0) {
                        toast.success(data.message || "Refund calculated successfully", { position: "top-center" });
                    } else {
                        const errorMsg = data.raw_response?.Error?.ErrorMessage ||
                            data.raw_response?.Response?.Error?.ErrorMessage ||
                            data.Error?.ErrorMessage ||
                            data.Response?.Error?.ErrorMessage ||
                            "Unable to Process Cancellation Charges Request.";
                        toast.error(errorMsg, { position: "top-center" });
                    }
                } else {
                    toast.success(data.message || "Refund calculated successfully", { position: "top-center" });
                }
            } else {
                toast.error(response.message || "Failed to calculate refund", { position: "top-center" });
            }
        } catch (error) {
            console.error("Refund Calculation Error:", error);
            toast.error(error.message || "Error calculating refund", { position: "top-center" });
        } finally {
            setCalculatingLoading(false);
        }
    };

    const handleSubmitCancellation = async () => {
        const tboBookingId = getTboBookingId();
        if (!tboBookingId) {
            toast.error("TBO Booking ID not found.", { position: "top-center" });
            return;
        }

        if (cancellationType === "others" && !remarks.trim()) {
            toast.error("Please provide remarks for 'Others' cancellation type", { position: "top-center" });
            return;
        }

        try {
            setSubmittingLoading(true);
            const user = auth.currentUser;
            if (!user) {
                toast.error("Please login to continue", { position: "top-center" });
                return;
            }
            const token = await user.getIdToken();

            // Map UI types to TBO types based on documentation
            // RequestType: 1=FullCancellation, 2=PartialCancellation
            const totalPass = passengers.length;
            const selectedPassCount = Object.values(selectedPassengers).filter(v => v).length;
            const tboRequestType = selectedPassCount === totalPass ? 1 : 2;

            // CancellationType: 1=NoShow, 2=FlightCancelled, 3=Others
            let tboCancellationType = 3; // Default to Others
            if (cancellationType === "noshow") {
                tboCancellationType = 1;
            } else if (cancellationType === "cancelled") {
                tboCancellationType = 2;
            }

            const ticketIds = passengers
                .filter((_, idx) => selectedPassengers[idx])
                .map(p => p.Ticket?.TicketId)
                .filter(id => id);

            // Sectors for partial cancellation
            const sectors = [];
            if (tboRequestType === 2 && segments.length > 0) {
                segments.forEach(seg => {
                    sectors.push({
                        Origin: seg.Origin?.Airport?.AirportCode,
                        Destination: seg.Destination?.Airport?.AirportCode
                    });
                });
            }

            const response = await sendChangeRequest({
                bookingId: tboBookingId,
                requestType: tboRequestType,
                cancellationType: tboCancellationType,
                remarks: remarks || cancellationOptions.find(o => o.value === cancellationType)?.name,
                ticketIds: ticketIds,
                sectors: sectors,
                userToken: token
            });

            if (response.status === 200) {
                const data = response.data || response;

                // Check if TBO response indicates failure even with HTTP 200
                const raw = data.raw_response || data;

                if (raw?.ResponseStatus === 2 || raw?.Response?.ResponseStatus === 2) {
                    const errorMsg = raw?.Error?.ErrorMessage ||
                        raw?.Response?.Error?.ErrorMessage ||
                        response.message ||
                        "Unable to Process Cancellation Request.";
                    toast.error(errorMsg, { position: "top-center" });
                } else {
                    toast.success(response.message || "Cancellation request submitted successfully", { position: "top-center" });
                    if (onSuccess) onSuccess();
                    onClose();
                }
            } else {
                toast.error(response.message || "Failed to submit cancellation", { position: "top-center" });
            }
        } catch (error) {
            console.error("Cancellation Submission Error:", error);
            toast.error(error.message || "Error submitting cancellation", { position: "top-center" });
        } finally {
            setSubmittingLoading(false);
        }
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <div className="fixed inset-0 z-[2000] flex justify-end">

                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="transition-opacity ease-linear duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
                        onClick={onClose}
                    />
                </Transition.Child>

                {/* Drawer Panel */}
                <Transition.Child
                    as={Fragment}
                    enter="transition transform ease-in-out duration-300"
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave="transition transform ease-in-out duration-300"
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                >
                    <div className="relative w-full max-w-[480px] bg-[#F1F3FC] h-full shadow-2xl flex flex-col pointer-events-auto">

                        {/* Header Content */}
                        <div className="px-6 pt-6 pb-4 bg-[#F1F3FC]">
                            <div className="flex justify-between items-center mb-2">
                                <div>
                                    {/* Title */}
                                    <h2 className="text-[#1A2233] text-lg font-bold uppercase tracking-tight">
                                        CANCEL FLIGHT / BOOKING
                                    </h2>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 -mr-2 rounded-full hover:bg-gray-200/50 transition-colors text-gray-800"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">

                            {/* Cancellation Rules */}
                            <div className="space-y-3">
                                <h3 className="text-[#556987] font-bold text-sm">Cancellation Rules</h3>
                                <div className="space-y-3 text-sm text-[#556987]">


                                    {/* Detailed Policy Information */}
                                    {itinerary?.FareRules?.[0]?.FareRuleDetail && (
                                        <div className={`rounded-xl p-3 border border-gray-100 bg-gray-50/50`}>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Detailed Policy Information</p>
                                            <div
                                                className="text-[12px] leading-relaxed opacity-90 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar"
                                                dangerouslySetInnerHTML={{
                                                    __html: itinerary.FareRules[0].FareRuleDetail
                                                        .replace(/\r\n/g, '<br/>')
                                                        .replace(/\n/g, '<br/>')
                                                }}
                                            />
                                        </div>
                                    )}

                                    {!itinerary?.MiniFareRules && !itinerary?.FareRules?.[0]?.FareRuleDetail && (
                                        <p className="text-gray-400 text-xs italic">No specific cancellation rules available for this booking.</p>
                                    )}

                                    <p className="text-gray-400 text-[11px] mt-1 pl-1 leading-tight italic">
                                        (Cancellation fee or airfare charged, whichever is lower)
                                    </p>
                                </div>
                            </div>


                            {/* Select Flight Section */}
                            <div className="pt-2">
                                <h3 className="text-[#1A2233] font-bold text-base mb-3">Select flight and passengers</h3>

                                {/* Flight Card */}
                                <div className="bg-white rounded-xl p-3 shadow-[0px_1px_3px_rgba(16,24,40,0.1)] border border-gray-200 mb-4 flex items-center justify-between">
                                    <div className="flex flex-col gap-3">
                                        <div className="flex items-center gap-3">
                                            <span className="font-medium text-[#101828] text-sm">{originCode}</span>
                                            <div className="h-[2px] w-5 bg-[#101828] rounded-full"></div>
                                            <span className="font-medium text-[#101828] text-sm">{destCode}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-[#475467] text-xs font-medium">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#667085]">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                            {(() => {
                                                const d = new Date(depDate);
                                                return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}/${d.toLocaleString('default', { weekday: 'short' })}`;
                                            })()}
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            className="w-5 h-5 border border-gray-300 rounded text-[#FF4E01] focus:ring-[#FF4E01] cursor-pointer accent-[#FF4E01] transition-all"
                                            checked={selectedFlight}
                                            onChange={handleSelectFlight}
                                        />
                                    </div>
                                </div>

                                {/* Passengers */}
                                <div className="relative pl-6 ml-5 border-l-[3px] border-[#FF4E01] space-y-3">
                                    {passengers.length > 0 ? passengers.map((pax, idx) => (
                                        <div key={idx} className="bg-white rounded-xl p-4 shadow-[0px_1px_3px_rgba(16,24,40,0.1)] border border-gray-200 flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-[#101828] text-lg">{pax.FirstName}</span>
                                                    <span className="text-[#667085] text-sm font-normal">{pax.PaxType === 1 ? 'Adult' : pax.PaxType === 2 ? 'Child' : 'Infant'}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">

                                                <input
                                                    type="checkbox"
                                                    className="w-5 h-5 border border-gray-300 rounded text-[#FF4E01] focus:ring-[#FF4E01] cursor-pointer accent-[#FF4E01] transition-all"
                                                    checked={!!selectedPassengers[idx]}
                                                    onChange={() => handlePassengerToggle(idx)}
                                                />
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                            <span className="text-gray-400 text-sm">No passenger details available</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Cancellation Type */}
                            <div className="pt-2">
                                <h3 className="text-[#1A2233] font-bold text-base mb-3">Cancellation Type</h3>
                                <Listbox value={cancellationType} onChange={setCancellationType}>
                                    <div className="relative">
                                        <Listbox.Button className="relative w-full bg-white border border-gray-200 rounded-xl py-3.5 pl-4 pr-10 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF4E01]/20 focus:border-[#FF4E01] transition-all font-medium text-[#101828] shadow-[0px_1px_2px_rgba(16,24,40,0.05)]">
                                            <span className="block truncate">
                                                {cancellationOptions.find(opt => opt.value === cancellationType)?.name || "Select Type"}
                                            </span>
                                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" strokeWidth="2" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                        </Listbox.Button>
                                        <Transition
                                            as={Fragment}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                        >
                                            <Listbox.Options className="absolute z-[100] mt-2 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-base shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-100">
                                                {cancellationOptions.map((option) => (
                                                    <Listbox.Option
                                                        key={option.id}
                                                        className={({ active }) =>
                                                            `relative cursor-pointer select-none py-3.5 px-4 transition-all duration-200 ${active ? 'bg-[#FFF5F1] text-[#FF4E01]' : 'text-gray-900'
                                                            }`
                                                        }
                                                        value={option.value}
                                                    >
                                                        {({ selected, active }) => (
                                                            <div className="flex items-center justify-between">
                                                                <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>
                                                                    {option.name}
                                                                </span>
                                                                {selected && (
                                                                    <span className="text-[#FF4E01] flex items-center">
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                                        </svg>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </Listbox.Option>
                                                ))}
                                            </Listbox.Options>
                                        </Transition>
                                    </div>
                                </Listbox>

                                {/* Refund Estimation (Below Cancellation Type) */}
                                {refundInfo && (
                                    <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <h3 className="text-[#1A2233] font-bold text-base mb-3">Refund Summary</h3>
                                        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-[0px_1px_3px_rgba(16,24,40,0.1)] flex justify-between items-center">
                                            <div>
                                                <p className="text-[#556987] text-[10px] font-bold uppercase tracking-wide opacity-70">Estimated Refund</p>
                                                <p className="text-xl font-black text-[#1A2233]">₹ {refundInfo.refund_amount || '0'}</p>
                                            </div>
                                            {refundInfo.cancellation_charges && (
                                                <div className="text-right">
                                                    <p className="text-[#556987] text-[9px] font-bold uppercase opacity-60">Cancellation Fee</p>
                                                    <p className="text-[#FF4E01] font-extrabold text-sm">₹ {refundInfo.cancellation_charges}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Remarks Field (Conditional) */}
                            {cancellationType === "others" && (
                                <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <h3 className="text-[#1A2233] font-bold text-base mb-3">Remarks <span className="text-red-500">*</span></h3>
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        placeholder="Please provide a reason for cancellation..."
                                        className="w-full bg-white border border-gray-200 rounded-xl py-3.5 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF4E01]/20 focus:border-[#FF4E01] transition-all font-medium min-h-[100px] shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
                                        required
                                    />
                                </div>
                            )}

                        </div>

                        {/* Footer */}
                        <div className="bg-[#F1F3FC] p-6 border-t border-white/40 flex items-center gap-4 relative z-10">
                            <button
                                onClick={handleCalculateRefund}
                                disabled={calculatingLoading || submittingLoading}
                                className={`flex-1 h-12 flex items-center justify-center bg-white border border-gray-200 rounded-3xl font-extrabold text-sm text-[#1A2233] hover:bg-gray-50 transition-all shadow-sm active:scale-[0.98] disabled:opacity-50`}
                            >
                                {calculatingLoading ? (
                                    <div className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Calculating...</span>
                                    </div>
                                ) : "Calculate refund"}
                            </button>

                            <button
                                onClick={handleSubmitCancellation}
                                disabled={calculatingLoading || submittingLoading}
                                className="flex-1 h-12 flex items-center justify-center bg-[#1A2233] rounded-3xl font-extrabold text-sm text-white hover:bg-[#111827] transition-all shadow-[0_8px_16px_-4px_rgba(26,34,51,0.2)] active:scale-[0.98] group disabled:opacity-50"
                            >
                                {submittingLoading ? (
                                    <div className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Submitting...</span>
                                    </div>
                                ) : (
                                    <>
                                        <span>Submit</span>
                                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Transition.Child>
            </div>
        </Transition>
    );
};

export default CancelBookingDrawer;
